import React, { useEffect } from 'react';
import { AlertIssue } from '../../AlertsAndNotification/store/types';
import { mapFieldRefToSelector } from '../../AlertsAndNotification/utils/fieldMapper';
import styles from './NotificationModal.module.scss';
import AlertTriangleIcon from '../../../assets/figma/alert-triangle-red-icon.svg';

/**
 * Feature-level date titles are server-composed as `${featureName} ${suffix}`
 * (see DatePastDueAlertRule.java) with no separate featureName field in the API response.
 * Gate the split on fieldRef mapping to the 'features' section first (structural, reliable),
 * then match a known suffix — so unrelated titles (headline/milestone/general-info) never split.
 */
const FEATURE_DATE_TITLE_SUFFIXES = [
  'GA date is past due',
  'Demo ETA is past due',
  'PRD ETA is past due'
];

interface FeatureIssueTitleParts {
  featureName: string;
  message: string;
}

const splitFeatureIssueTitle = (
  issue: AlertIssue
): FeatureIssueTitleParts | null => {
  const mapping = mapFieldRefToSelector(issue.fieldRef, issue.ruleId);
  if (mapping?.section !== 'features') return null;

  const suffix = FEATURE_DATE_TITLE_SUFFIXES.find(s => issue.title.endsWith(s));
  if (!suffix) return null;

  const featureName = issue.title.slice(0, -suffix.length).trim();
  if (!featureName) return null;

  return { featureName, message: suffix };
};

interface NotificationModalProps {
  isOpen: boolean;
  onClose: () => void;
  issues: AlertIssue[];
  momentName: string;
  /** Wrapper that contains the bell + this popover — used for outside-click detection */
  popoverContainerRef: React.RefObject<HTMLElement | null>;
  /** Called when user clicks an issue — scroll to the highlighted field and close modal */
  onIssueClick?: (issue: AlertIssue) => void;
}

const NotificationModal: React.FC<NotificationModalProps> = ({
  isOpen,
  onClose,
  issues,
  momentName: _momentName,
  popoverContainerRef,
  onIssueClick
}) => {
  // Close on Escape key when modal is open
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  // Close on outside click. Slideout/comments may sit above a backdrop — use document mousedown.
  useEffect(() => {
    if (!isOpen) return;
    const handlePointerDown = (e: MouseEvent) => {
      const target = e.target as Node | null;
      if (!target) return;
      if (popoverContainerRef.current?.contains(target)) return;
      onClose();
    };
    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, [isOpen, onClose, popoverContainerRef]);

  if (!isOpen) return null;

  return (
    <div
      className={styles.modalContainer}
      role="dialog"
      aria-modal="true"
      aria-labelledby="notification-modal-title"
      data-testid="notification-modal-container"
      onClick={e => e.stopPropagation()}
    >
      <span id="notification-modal-title" className={styles.visuallyHidden}>
        Notifications
      </span>
      <div
        className={styles.modalContent}
        data-testid="notification-modal-content"
      >
        {issues.length === 0 ? (
          <div className={styles.emptyState}>
            <p>No alerts for this moment.</p>
          </div>
        ) : (
          <div className={styles.issuesList}>
            {issues.map((issue, index) => {
              const featureParts = splitFeatureIssueTitle(issue);
              return (
                <React.Fragment key={issue.issueKey || index}>
                  <div
                    className={`${styles.issueItem} ${
                      onIssueClick ? styles.issueItemClickable : ''
                    }`}
                    data-testid={`notification-issue-${index}`}
                    onClick={() => onIssueClick?.(issue)}
                    onKeyDown={e => {
                      if (
                        onIssueClick &&
                        (e.key === 'Enter' || e.key === ' ')
                      ) {
                        e.preventDefault();
                        onIssueClick(issue);
                      }
                    }}
                    role={onIssueClick ? 'button' : undefined}
                    tabIndex={onIssueClick ? 0 : undefined}
                  >
                    <div className={styles.issueContent}>
                      {featureParts && (
                        <p
                          className={styles.issueFeatureName}
                          data-testid={`notification-issue-feature-${index}`}
                        >
                          <span className={styles.issueFeatureLabel}>
                            Feature:{' '}
                          </span>
                          <span className={styles.issueFeatureValue}>
                            {featureParts.featureName}
                          </span>
                        </p>
                      )}
                      <p className={styles.issueTitle}>
                        {featureParts ? featureParts.message : issue.title}
                      </p>
                    </div>
                    <div className={styles.issueIcon}>
                      <img
                        src={AlertTriangleIcon}
                        alt="Alert"
                        width="18"
                        height="18"
                      />
                    </div>
                  </div>
                  {index < issues.length - 1 && (
                    <div className={styles.divider} />
                  )}
                </React.Fragment>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default NotificationModal;
